import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  OnboardingNavigationState,
  readOnboardingNavigationState,
} from '@/pages/onboarding/onboarding-navigation-state';
import { motion, useReducedMotion } from 'motion/react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { usePatchTenantCreationRequest } from '@/hooks/use-patch-tenant-request';
import { useTenantCreationRequest } from '@/hooks/use-tenant-creation-request';
import {
  OnboardingPreviewProvider,
  useOnboardingPreview,
  useSetOnboardingPreview,
} from '@/pages/onboarding/onboarding-preview-context';
import {
  ZoomedIPhoneMockup,
  ZOOM_TRANSITION_DURATION_MS,
} from '@/pages/onboarding/mockup/zoomed-iphone-mockup';
import { useMediaQuery } from '@/hooks/use-mobile';
import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';
import { AppOpenScreen } from '@/pages/onboarding/mockup/scenes/app-open-scene';
import {
  DEFAULT_ACCENT_COLOR,
  PRESET_TABS,
} from '@/pages/onboarding/4-tab-bar-config.schema';
import {
  HOME_ICON_FOCUS,
  TAB_BAR_FOCUS,
} from '@/pages/onboarding/mobile-studio-geometry';
import {
  APP_OPEN_DURATION_MS,
  HomeScreenScene,
} from '@/pages/onboarding/mockup/scenes/home-screen-scene';
import {
  OnboardingPageLayout,
  OnboardingPageHeader,
  OnboardingPageContent,
  OnboardingPageFooter,
} from '@/pages/onboarding/onboarding-page-layout';
import { LogoFramingCard } from '@/pages/onboarding/logo-framing-card';
import { OnboardingStepsIndicator } from '@/pages/onboarding/onboarding-steps-indicator';
import { renderPlaceholderAppIcon } from '@/lib/placeholder-app-icon';

// How long after mount the zoom-in starts when arriving back from the
// tab-bar step (see `arrivingFromTabBarBack` below) — same rationale as
// AppNameAndLinkPage's ZOOM_IN_DELAY_MS.
const ZOOM_IN_DELAY_MS = 500;

// Past the page transition (the browser's 250ms crossfade) when arriving back
// from the tab-bar step below lg, so the app visibly closes into its icon.
const APP_CLOSE_DELAY_MS = 350;

export default function AppLogoPage() {
  return (
    <OnboardingPreviewProvider>
      <AppLogoPageContent />
    </OnboardingPreviewProvider>
  );
}

function AppLogoPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const patchCreationRequest = usePatchTenantCreationRequest();
  const persistedRequest = useTenantCreationRequest();
  const preview = useOnboardingPreview();
  const setPreview = useSetOnboardingPreview();
  const shouldReduceMotion = useReducedMotion();

  // Normally this page arrives already zoomed in, continuing visually from
  // AppNameAndLinkPage (which leaves the phone zoomed when navigating
  // forward, no re-animation needed here). But arriving *back* from the
  // tab-bar step is different: TabBarConfigPage's onBack zooms itself out to
  // rest before navigating, so the browser's View Transition API can morph
  // its full-screen AppOpenScene back down into this page's (resting) icon
  // — which means this page has to render at rest on that first paint too,
  // then replay its own zoom-in afterwards instead of rendering zoomed from
  // the very first frame.
  //
  // Below lg none of that: the Studio stays framed on the icon, which grows
  // into (and shrinks back from) the open app from there, so leaving
  // or arriving never zooms the phone out and back in.
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);
  const fromTabBarBack = Boolean(
    readOnboardingNavigationState(location.state).fromTabBarBack
  );
  const arrivingFromTabBarBack = isDesktop && fromTabBarBack;
  const [isZoomed, setIsZoomed] = useState(!arrivingFromTabBarBack);
  // Below lg the app opens and closes inside this page's phone, in three
  // beats: the Studio zooms out to the whole phone, the app opens from its
  // icon, then the Studio zooms in on the tab bar, where the next step
  // starts. Arriving back from that step it starts open and framed on the
  // tab bar, as that step left it, and plays the same beats in reverse:
  // zoom out, the app closes into its icon, zoom in on the icon.
  const arrivingOpen = !isDesktop && fromTabBarBack;
  const [isAppOpen, setIsAppOpen] = useState(arrivingOpen);
  const [isFramedOnTabBar, setIsFramedOnTabBar] = useState(arrivingOpen);
  useEffect(() => {
    if (!arrivingOpen) return;
    if (shouldReduceMotion) {
      setIsFramedOnTabBar(false);
      setIsAppOpen(false);
      return;
    }
    // After the page transition: moved any sooner, nothing is drawn yet and
    // nothing animates.
    const zoomOut = APP_CLOSE_DELAY_MS;
    const close = zoomOut + ZOOM_TRANSITION_DURATION_MS;
    const zoomIn = close + APP_OPEN_DURATION_MS;
    const timeouts = [
      setTimeout(() => {
        setIsFramedOnTabBar(false);
        setIsZoomed(false);
      }, zoomOut),
      setTimeout(() => setIsAppOpen(false), close),
      setTimeout(() => setIsZoomed(true), zoomIn),
    ];
    return () => timeouts.forEach(clearTimeout);
  }, [arrivingOpen, shouldReduceMotion]);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (!arrivingFromTabBarBack) {
      return;
    }
    if (shouldReduceMotion) {
      setIsZoomed(true);
      return;
    }
    const timeout = setTimeout(() => setIsZoomed(true), ZOOM_IN_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [arrivingFromTabBarBack, shouldReduceMotion]);

  const [framedLogoFile, setFramedLogoFile] = useState<File | null>(null);
  const [localLogoUrl, setLocalLogoUrl] = useState<string | null>(null);

  const onFramedLogoChange = (file: File | null) => {
    setFramedLogoFile(file);

    if (localLogoUrl) {
      URL.revokeObjectURL(localLogoUrl);
    }

    if (!file) {
      setLocalLogoUrl(null);
      // Falls back to the actually-persisted icon, not the live preview
      // override (which, by this point, is the blob URL we just revoked).
      setPreview({ appIconUrl: persistedRequest.appIconUrl ?? null });
      return;
    }

    const url = URL.createObjectURL(file);
    setLocalLogoUrl(url);
    setPreview({ appIconUrl: url });
  };

  const leaveWith = async (appIcon: File | null) => {
    if (appIcon) {
      await patchCreationRequest.mutateAsync({ appIcon });
    }
    setIsLeaving(true);
    // Zoom back out to rest *before* navigating: the "icon grows into the
    // open app" effect on the tab-bar step is the browser's View Transition
    // API morphing this page's icon into that page's full-screen
    // AppOpenScene, and that only looks right if this page's icon is at its
    // normal (unzoomed) size at the moment the navigation snapshot is taken.
    // Below lg the app then opens inside this page's phone and the Studio
    // zooms in on the tab bar, so the next step starts where this one ends.
    const wait = (ms: number) =>
      new Promise((resolve) =>
        setTimeout(resolve, shouldReduceMotion ? 0 : ms)
      );
    setIsZoomed(false);
    await wait(ZOOM_TRANSITION_DURATION_MS);
    if (!isDesktop) {
      setIsAppOpen(true);
      await wait(APP_OPEN_DURATION_MS);
      setIsFramedOnTabBar(true);
      await wait(ZOOM_TRANSITION_DURATION_MS);
    }
    navigate('../tab-bar', { viewTransition: true });
  };

  const onContinue = () => leaveWith(framedLogoFile);

  // Below lg only: the app gets the app name's initial as its icon, which
  // the merchant swaps for the logo later in "Seu app".
  const onFillLater = async () => {
    const appName = preview.appName || persistedRequest.appName || '';
    await leaveWith(await renderPlaceholderAppIcon(appName));
  };

  const hasExistingLogo = !!preview.appIconUrl && !framedLogoFile;

  return (
    <OnboardingPageLayout
      mobileFocus={
        isFramedOnTabBar
          ? TAB_BAR_FOCUS
          : isZoomed
            ? HOME_ICON_FOCUS
            : undefined
      }
      mockup={
        <ZoomedIPhoneMockup sceneKey="home" chromeBaked zoomed={isZoomed}>
          <HomeScreenScene
            appName={preview.appName}
            appIconUrl={localLogoUrl ?? preview.appIconUrl}
            openApp={
              isDesktop
                ? undefined
                : {
                    opened: isAppOpen,
                    screen: (
                      <AppOpenScreen
                        tabs={
                          preview.tabs && preview.tabs.length > 0
                            ? preview.tabs
                            : PRESET_TABS
                        }
                        accentColor={
                          preview.accentColor ?? DEFAULT_ACCENT_COLOR
                        }
                      />
                    ),
                  }
            }
          />
        </ZoomedIPhoneMockup>
      }
    >
      <OnboardingStepsIndicator currentStep="app-logo" />

      <motion.div
        initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground max-lg:hidden">
          <Sparkles className="h-3 w-3" />
          Identidade do app
        </span>
        <OnboardingPageHeader
          title="Agora, a logo do seu app"
          subtitle="Ajuste o enquadramento até a logo encaixar perfeitamente no ícone do app."
        />
      </motion.div>

      <OnboardingPageContent>
        <motion.div
          initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08 }}
        >
          {/* Below lg the form sits in the Studio's sheet, already a surface of
              its own: no card chrome, and no card title repeating the page
              title, so the fields fit above the fold. */}
          <Card className="rounded-2xl border-border/60 shadow-sm transition-shadow focus-within:shadow-md focus-within:shadow-primary/5 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none max-lg:focus-within:shadow-none">
            <CardHeader className="max-lg:hidden">
              <CardTitle>Logo do app</CardTitle>
            </CardHeader>
            <CardContent className="max-lg:p-0">
              <LogoFramingCard
                // The *persisted* icon, not `preview.appIconUrl` — that one
                // also reflects this card's own local edits, and feeding
                // those back in as `existingLogoUrl` would have the card
                // re-load its own freshly-rendered output as if it were a
                // different "existing" image, re-triggering itself.
                existingLogoUrl={persistedRequest.appIconUrl}
                onChange={onFramedLogoChange}
                disabled={patchCreationRequest.isPending}
              />
            </CardContent>
          </Card>
          {!framedLogoFile && !hasExistingLogo && (
            <div className="mt-1 flex justify-center lg:hidden">
              <Button
                type="button"
                variant="link"
                disabled={patchCreationRequest.isPending || isLeaving}
                onClick={onFillLater}
              >
                Preencher depois
              </Button>
            </div>
          )}
        </motion.div>
      </OnboardingPageContent>

      <OnboardingPageFooter className="justify-between">
        <Button
          type="button"
          variant="outline"
          disabled={patchCreationRequest.isPending || isLeaving}
          onClick={() =>
            navigate('../app-name-link', {
              viewTransition: true,
              state: {
                fromAppLogoBack: true,
              } satisfies OnboardingNavigationState,
            })
          }
        >
          Voltar
        </Button>
        <Button
          type="button"
          disabled={
            (!framedLogoFile && !hasExistingLogo) ||
            patchCreationRequest.isPending ||
            isLeaving
          }
          className="min-w-[100px]"
          onClick={onContinue}
        >
          {patchCreationRequest.isPending || isLeaving ? (
            <Loader2 className="animate-spin" />
          ) : (
            'Continuar'
          )}
        </Button>
      </OnboardingPageFooter>
    </OnboardingPageLayout>
  );
}
